import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { cx } from '../cx';

/**
 * Marcar áreas sobre uma imagem: retângulo, contorno ponto a ponto e à mão livre, com ajuste de pontos, mover e um quadro
 * de recorte. Tudo em fração da imagem (0 a 1), para valer em qualquer tamanho de tela. Quem usa decide o que a área
 * significa (a galeria usa para áreas protegidas); aqui é só a geometria.
 */
export type MarkerShape = { kind: 'rect' | 'polygon'; points: [number, number][] };
export type MarkerArea = { id: string; shape: MarkerShape; margin: number; tone: 'active' | 'suggested' | 'forbid'; badge: string };
export type MarkerTool = 'select' | 'rect' | 'polygon' | 'lasso';
export type MarkerCrop = { x: number; y: number; w: number; h: number };

type Pt = [number, number];
type Drag =
  | { type: 'move'; id: string; last: Pt; shape: MarkerShape }
  | { type: 'vertex'; id: string; i: number; shape: MarkerShape }
  | { type: 'corner'; id: string; opp: Pt; shape: MarkerShape }
  | { type: 'crop'; off: Pt }
  | { type: 'cropSize' };

const clamp = (n: number) => Math.min(1, Math.max(0, n));
function rectPoints(a: Pt, b: Pt): Pt[] { const x0 = Math.min(a[0], b[0]), y0 = Math.min(a[1], b[1]), x1 = Math.max(a[0], b[0]), y1 = Math.max(a[1], b[1]); return [[x0, y0], [x1, y0], [x1, y1], [x0, y1]]; }
function box(P: Pt[]) { let x0 = 1, y0 = 1, x1 = 0, y1 = 0; for (const [x, y] of P) { x0 = Math.min(x0, x); y0 = Math.min(y0, y); x1 = Math.max(x1, x); y1 = Math.max(y1, y); } return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 }; }
function area(P: Pt[]) { let s = 0; for (let i = 0; i < P.length; i++) { const [x1, y1] = P[i]!, [x2, y2] = P[(i + 1) % P.length]!; s += x1 * y2 - x2 * y1; } return Math.abs(s / 2); }
function segDist(p: Pt, a: Pt, b: Pt) { const dx = b[0] - a[0], dy = b[1] - a[1], l = dx * dx + dy * dy; const t = l ? Math.max(0, Math.min(1, ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / l)) : 0; return Math.hypot(p[0] - a[0] - t * dx, p[1] - a[1] - t * dy); }
function simplify(P: Pt[], eps: number): Pt[] {
  if (P.length < 3) return P;
  let dmax = 0, idx = 0;
  const end = P.length - 1;
  for (let i = 1; i < end; i++) { const d = segDist(P[i]!, P[0]!, P[end]!); if (d > dmax) { dmax = d; idx = i; } }
  if (dmax > eps) return simplify(P.slice(0, idx + 1), eps).slice(0, -1).concat(simplify(P.slice(idx), eps));
  return [P[0]!, P[end]!];
}

export function AreaMarker({
  src, alt, areas, selected, tool, onSelect, onCreate, onChange, onRemovePoint, crop, cropRatio, onCrop, onAspect, hideAreas, onToolDone,
}: {
  src: string; alt?: string; areas: MarkerArea[]; selected: string | null; tool: MarkerTool;
  onSelect: (id: string | null) => void; onCreate: (shape: MarkerShape) => void; onChange: (id: string, shape: MarkerShape) => void;
  onRemovePoint?: (id: string, shape: MarkerShape) => void;
  /** com recorte: o quadro aparece por cima e só ele se mexe; `cropRatio` = largura ÷ altura em pixels */
  crop?: MarkerCrop | null; cropRatio?: number; onCrop?: (c: MarkerCrop) => void;
  onAspect?: (aspect: number) => void; hideAreas?: boolean; onToolDone?: () => void;
}) {
  const ref = useRef<SVGSVGElement>(null);
  const [aspect, setAspect] = useState(1);
  const [draft, setDraft] = useState<{ kind: 'rect'; a: Pt; b: Pt } | { kind: 'polygon' | 'lasso'; pts: Pt[] } | null>(null);
  const [cursor, setCursor] = useState<Pt | null>(null);
  const [drag, setDrag] = useState<Drag | null>(null);
  // a forma de quem está sendo arrastado, antes de soltar (o pai só recebe ao soltar)
  const [live, setLive] = useState<{ id: string; shape: MarkerShape } | null>(null);
  const W = 1000, H = 1000 / aspect;
  const px = (n: number) => n * (W / Math.max(1, ref.current?.clientWidth ?? W)); // pixels de tela em unidades do desenho
  const at = (e: { clientX: number; clientY: number }): Pt => { const r = ref.current!.getBoundingClientRect(); return [clamp((e.clientX - r.left) / r.width), clamp((e.clientY - r.top) / r.height)]; };
  const S = (p: Pt) => `${p[0] * W},${p[1] * H}`;
  const shapeOf = (id: string) => (live?.id === id ? live.shape : areas.find((a) => a.id === id)?.shape);

  useEffect(() => { setDraft(null); }, [tool]);
  const finishPolygon = (pts: Pt[]) => {
    setDraft(null);
    if (pts.length >= 3 && area(pts) > 0.00005) { onCreate({ kind: 'polygon', points: pts }); onToolDone?.(); }
  };
  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      if ((e.target as HTMLElement)?.closest('input, textarea, select, [contenteditable]')) return;
      if (e.key === 'Escape' && draft) { e.stopImmediatePropagation(); setDraft(null); }
      if (e.key === 'Enter' && draft?.kind === 'polygon') finishPolygon(draft.pts);
    };
    window.addEventListener('keydown', key, true);
    return () => window.removeEventListener('keydown', key, true);
  });

  const down = (e: ReactPointerEvent<SVGSVGElement>) => {
    if (e.button > 0) return;
    const p = at(e);
    const t = (e.target as Element).closest('[data-h],[data-area]') as HTMLElement | SVGElement | null;
    const h = t?.getAttribute('data-h') ?? null, areaId = t?.getAttribute('data-area') ?? null;
    ref.current!.setPointerCapture(e.pointerId);
    if (crop && onCrop) {
      if (h === 'crop-size') setDrag({ type: 'cropSize' });
      else if (h === 'crop') setDrag({ type: 'crop', off: [p[0] - crop.x, p[1] - crop.y] });
      else { const c = { ...crop, x: clamp(p[0] - crop.w / 2), y: clamp(p[1] - crop.h / 2) }; onCrop(fit(c)); setDrag({ type: 'crop', off: [crop.w / 2, crop.h / 2] }); }
      return;
    }
    if (tool === 'select') {
      const sel = selected ? shapeOf(selected) : null;
      if (h && sel && selected) {
        const [kind, n] = h.split(':');
        const i = Number(n);
        if (kind === 'mid') { const pts = [...sel.points]; pts.splice(i + 1, 0, p); const shape = { ...sel, points: pts }; setLive({ id: selected, shape }); setDrag({ type: 'vertex', id: selected, i: i + 1, shape }); }
        else if (kind === 'v' && sel.kind === 'polygon') setDrag({ type: 'vertex', id: selected, i, shape: sel });
        else if (kind === 'v') setDrag({ type: 'corner', id: selected, opp: sel.points[(i + 2) % 4]!, shape: sel });
        return;
      }
      if (areaId) { onSelect(areaId); const s = shapeOf(areaId); if (s) setDrag({ type: 'move', id: areaId, last: p, shape: s }); return; }
      onSelect(null);
      return;
    }
    if (tool === 'rect') setDraft({ kind: 'rect', a: p, b: p });
    else if (tool === 'lasso') setDraft({ kind: 'lasso', pts: [p] });
    else if (tool === 'polygon') {
      if (!draft || draft.kind !== 'polygon') return setDraft({ kind: 'polygon', pts: [p] });
      const first = draft.pts[0]!, last = draft.pts[draft.pts.length - 1]!;
      const near = (a: Pt, b: Pt, n: number) => Math.hypot((a[0] - b[0]) * W, (a[1] - b[1]) * H) < px(n);
      if (draft.pts.length >= 3 && near(p, first, 12)) return finishPolygon(draft.pts);
      if (!near(p, last, 3)) setDraft({ kind: 'polygon', pts: [...draft.pts, p] });
    }
  };
  const fit = (c: MarkerCrop): MarkerCrop => { const w = Math.min(1, c.w), hh = Math.min(1, c.h); return { w, h: hh, x: Math.min(1 - w, Math.max(0, c.x)), y: Math.min(1 - hh, Math.max(0, c.y)) }; };
  const move = (e: ReactPointerEvent<SVGSVGElement>) => {
    const p = at(e);
    setCursor(p);
    if (crop && onCrop && drag) {
      if (drag.type === 'crop') onCrop(fit({ ...crop, x: p[0] - drag.off[0], y: p[1] - drag.off[1] }));
      else if (drag.type === 'cropSize') {
        const r = (cropRatio ?? 1) / aspect; // largura ÷ altura em fração
        let w = Math.max(0.08, p[0] - crop.x);
        w = Math.min(w, 1 - crop.x, (1 - crop.y) * r);
        onCrop(fit({ ...crop, w, h: w / r }));
      }
      return;
    }
    if (draft) {
      if (draft.kind === 'rect') setDraft({ ...draft, b: p });
      else if (draft.kind === 'lasso' && e.buttons) { const l = draft.pts[draft.pts.length - 1]!; if (Math.hypot((p[0] - l[0]) * W, (p[1] - l[1]) * H) > px(4)) setDraft({ ...draft, pts: [...draft.pts, p] }); }
      return;
    }
    if (!drag || drag.type === 'crop' || drag.type === 'cropSize') return;
    const cur = live?.id === drag.id ? live.shape : drag.shape;
    if (drag.type === 'move') {
      const b = box(cur.points);
      const dx = Math.max(-b.x, Math.min(1 - b.x - b.w, p[0] - drag.last[0])), dy = Math.max(-b.y, Math.min(1 - b.y - b.h, p[1] - drag.last[1]));
      setLive({ id: drag.id, shape: { ...cur, points: cur.points.map(([x, y]) => [x + dx, y + dy] as Pt) } });
      setDrag({ ...drag, last: [drag.last[0] + dx, drag.last[1] + dy] });
    } else if (drag.type === 'vertex') {
      const pts = [...cur.points]; pts[drag.i] = p;
      setLive({ id: drag.id, shape: { ...cur, points: pts } });
    } else if (drag.type === 'corner') setLive({ id: drag.id, shape: { kind: 'rect', points: rectPoints(drag.opp, p) } });
  };
  const up = () => {
    if (draft?.kind === 'rect') {
      const pts = rectPoints(draft.a, draft.b), b = box(pts);
      setDraft(null);
      if (b.w * W > px(10) && b.h * H > px(10)) { onCreate({ kind: 'rect', points: pts }); onToolDone?.(); }
    } else if (draft?.kind === 'lasso') {
      const pts = simplify(draft.pts, px(2.5) / W);
      setDraft(null);
      if (pts.length >= 3 && area(pts) > 0.0002) { onCreate({ kind: 'polygon', points: pts }); onToolDone?.(); }
    }
    if (live && drag && (drag.type === 'move' || drag.type === 'vertex' || drag.type === 'corner')) onChange(live.id, live.shape);
    setLive(null);
    setDrag(null);
  };
  const context = (e: React.MouseEvent) => {
    const h = (e.target as Element).closest('[data-h]')?.getAttribute('data-h');
    const sel = selected ? shapeOf(selected) : null;
    if (!h?.startsWith('v:') || !sel || !selected || sel.kind !== 'polygon' || sel.points.length <= 3) return;
    e.preventDefault();
    const pts = sel.points.filter((_, i) => i !== Number(h.split(':')[1]));
    (onRemovePoint ?? onChange)(selected, { ...sel, points: pts });
  };

  const big = Math.max(W, H);
  const r = px(5.5);
  return (
    <div className={cx('rr-marker', crop && 'rr-marker--crop', tool !== 'select' && !crop && 'rr-marker--draw')}>
      <img src={src} alt={alt ?? ''} draggable={false} onLoad={(e) => { const i = e.currentTarget; const a = i.naturalWidth / Math.max(1, i.naturalHeight); setAspect(a); onAspect?.(a); }} />
      <svg ref={ref} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerLeave={() => setCursor(null)} onDoubleClick={() => { if (draft?.kind === 'polygon') finishPolygon(draft.pts.slice(0, -1).length >= 3 ? draft.pts.slice(0, -1) : draft.pts); }} onContextMenu={context}>
        <defs>
          <pattern id="rr-marker-hatch" width={px(8)} height={px(8)} patternUnits="userSpaceOnUse" patternTransform="rotate(45)"><line x1="0" y1="0" x2="0" y2={px(8)} className="rr-marker__hatch" strokeWidth={px(2)} /></pattern>
        </defs>
        {!hideAreas && areas.map((a) => {
          const s = live?.id === a.id ? live.shape : a.shape;
          const d = s.points.map(S).join(' ');
          const b = box(s.points);
          const on = a.id === selected;
          return (
            <g key={a.id} className={cx('rr-marker__area', `rr-marker__area--${a.tone}`, on && 'is-on')}>
              {a.margin > 0 && <polygon points={d} className="rr-marker__margin" strokeWidth={a.margin * big * 2} strokeLinejoin="round" />}
              <polygon points={d} className="rr-marker__fill" data-area={a.id} />
              {a.tone === 'forbid' && <polygon points={d} fill="url(#rr-marker-hatch)" pointerEvents="none" />}
              <polygon points={d} className="rr-marker__edge" strokeWidth={px(on ? 2 : 1.5)} pointerEvents="none" />
              <g transform={`translate(${b.x * W + px(4)},${b.y * H + px(4)})`} pointerEvents="none">
                <rect width={px(20)} height={px(18)} rx={px(3)} className="rr-marker__badge" />
                <text x={px(10)} y={px(13)} fontSize={px(11)} textAnchor="middle" className="rr-marker__badge-ink">{a.badge}</text>
              </g>
              {on && tool === 'select' && !crop && <>
                {s.kind === 'polygon' && s.points.map((p, i) => { const q = s.points[(i + 1) % s.points.length]!; return <circle key={`m${i}`} data-h={`mid:${i}`} cx={((p[0] + q[0]) / 2) * W} cy={((p[1] + q[1]) / 2) * H} r={r * 0.75} className="rr-marker__mid" />; })}
                {s.points.map((p, i) => s.kind === 'rect'
                  ? <rect key={`v${i}`} data-h={`v:${i}`} x={p[0] * W - r} y={p[1] * H - r} width={r * 2} height={r * 2} className="rr-marker__handle" />
                  : <circle key={`v${i}`} data-h={`v:${i}`} cx={p[0] * W} cy={p[1] * H} r={r} className="rr-marker__handle" />)}
              </>}
            </g>
          );
        })}
        {draft?.kind === 'rect' && <polygon points={rectPoints(draft.a, draft.b).map(S).join(' ')} className="rr-marker__draft" strokeWidth={px(1.5)} />}
        {draft?.kind === 'lasso' && <polyline points={draft.pts.map(S).join(' ')} className="rr-marker__draft" strokeWidth={px(1.5)} />}
        {draft?.kind === 'polygon' && <>
          <polyline points={[...draft.pts, ...(cursor ? [cursor] : [])].map(S).join(' ')} className="rr-marker__draft" strokeWidth={px(1.5)} />
          {draft.pts.map((p, i) => <circle key={i} cx={p[0] * W} cy={p[1] * H} r={px(i === 0 ? 6 : 3.5)} className={cx('rr-marker__dot', i === 0 && 'is-first')} />)}
        </>}
        {crop && <>
          <path d={`M0,0H${W}V${H}H0Z M${crop.x * W},${crop.y * H}v${crop.h * H}h${crop.w * W}v${-crop.h * H}Z`} fillRule="evenodd" className="rr-marker__shade" pointerEvents="none" />
          <rect data-h="crop" x={crop.x * W} y={crop.y * H} width={crop.w * W} height={crop.h * H} className="rr-marker__crop" strokeWidth={px(2)} />
          <rect data-h="crop-size" x={(crop.x + crop.w) * W - px(9)} y={(crop.y + crop.h) * H - px(9)} width={px(18)} height={px(18)} className="rr-marker__handle" />
        </>}
      </svg>
    </div>
  );
}
